import md5 from "md5";
import * as React from "react";
import { AutocompleteResults } from "../../../containers/AutocompleteResultsContainer";
import { useMultiHangarState } from "../../../containers/MultiHangarContainer";
import { useSnackbar } from "../../../containers/SnackbarContainer";
import { Preferences } from "../../../hooks/useUserPreferences";
import { getOrientation } from "../../../screens/Hangar/HangarLayout/utils";
import { HOLDING_AREA_ID } from "../../../screens/MultiHangar/HoldingArea";
import { AutocompletePosition, Stack, Tenant } from "../../../types";

type Props = {
  hangarId: string;
  enabled: boolean;
  stack: Stack;
  preferences: Preferences;
  isInLayout: (tenant: Tenant) => boolean;
  autocomplete?: (tenant: Tenant) => Promise<AutocompletePosition[]>;
  autocompleteResults: AutocompleteResults;
  setAutocompleteResults: (results: AutocompleteResults) => void;
};

export const useAutocompleteTenant = (props: Props, deps: any[] = []) => {
  const snackbar = useSnackbar();
  const { isMultiHangar, hangars } = useMultiHangarState();
  const {
    hangarId,
    enabled,
    stack,
    preferences,
    isInLayout,
    autocomplete,
    autocompleteResults,
    setAutocompleteResults,
  } = props;

  return React.useCallback(async (tenant: Tenant) => {
    if (!enabled) {
      return;
    }
    if (!preferences.enableCoPilot || !autocomplete) {
      return;
    }
    if (isInLayout(tenant) && hangarId !== HOLDING_AREA_ID) {
      return;
    }
    // Prevent concurrent autocomplete runs (fixes infinite notification loop)
    if (autocompleteResults?.loading) {
      return;
    }
    const hash = md5(
      JSON.stringify(isMultiHangar ? hangars.map((h) => h.stack) : stack)
    );
    const currentOrientation = getOrientation(tenant);
    // need to also test that our cached results are still valid based on the
    // other tenants
    if (
      autocompleteResults?.id === tenant.id &&
      currentOrientation === autocompleteResults?.orientation &&
      hash === autocompleteResults?.hash
    ) {
      return;
    }

    // marker to indicate we've started the autocomplete process
    setAutocompleteResults({
      id: tenant.id,
      hash,
      tenant,
      loading: true,
      orientation: currentOrientation,
      suggestedPositions: [],
    });

    const suggestedPositions = await autocomplete(tenant);
    setAutocompleteResults({
      id: tenant.id,
      hash,
      tenant,
      loading: false,
      orientation: currentOrientation,
      suggestedPositions,
    });

    if (suggestedPositions.length === 0) {
      snackbar.notify({
        text: `No viable positions found.`,
        severity: "warning",
      });
    }
  }, deps);
};
